import { sanitizeRedirectUrl } from '@/utils/browser';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useMemo } from 'react';

export const authRedirectQueryString = 'redirect';

// The trailing slash excludes only the token-bearing activation path, so the
// token never reaches an Auth0 redirect_uri while the token-free path still gets one.
const routeExceptions = [
    routes.sso,
    routes.ssoInfo,
    routes.saml,
    `${routes.partnerMemberActivation}/`,
];

function getAuthRedirectQueryString(query: ParsedUrlQuery) {
    const { [authRedirectQueryString]: redirectString } = query;
    return sanitizeRedirectUrl(redirectString as string, routes.dashboard);
}

export function getAuthRedirectQueryValue(
    query: ParsedUrlQuery,
    parameter: string
) {
    return getRedirectStringQueryValue(
        getAuthRedirectQueryString(query),
        parameter
    );
}

export function getRedirectStringQueryValue(
    redirectString: string,
    parameter: string
) {
    return new URL('http://example.com' + redirectString).searchParams.get(
        parameter
    );
}

const useAuthRedirect = () => {
    const { query } = useRouter();

    const createRedirectQueryString = useCallback((href?: string) => {
        const redirect = href
            ? href
            : window.location.href.split(window.location.host)[1]; // relative URL
        const isExcludedRoute = routeExceptions.some((route) =>
            redirect.startsWith(route)
        );
        return isExcludedRoute
            ? ''
            : `${authRedirectQueryString}=${encodeURIComponent(redirect)}`;
    }, []);

    const redirect = useMemo(() => getAuthRedirectQueryString(query), [query]);

    return {
        redirect,
        createRedirectQueryString,
    };
};

export default useAuthRedirect;
